import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AU_STATES, fmtDate, fmtDateTime, type AuState } from '@em/shared';
import { api, qk, queryClient, useSettings } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { PromptDialog } from '../../components/PromptDialog';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Field, Input, QueryState, Select, Textarea } from '../../components/ui';

/* ------------------------------------------------------------ holidays */

export function HolidaysSettings() {
  const settings = useSettings();
  const [state, setState] = useState<AuState | ''>('');
  const [year, setYear] = useState(new Date().getFullYear());
  const st = (state || settings.data?.defaultState || 'NSW') as AuState;
  const hq = { state: st, year };
  const q = useQuery({ queryKey: qk.holidays(hq), queryFn: () => api.publicHolidays(hq), enabled: !!settings.data });
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [addError, setAddError] = useState<unknown>(null);
  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['holidays'] });
  };

  return (
    <div className="stack">
      <Alert tone="amber" title="Check against the state gazette">
        “Import usual holidays” loads the regular gazetted days only. Holidays set by annual proclamation (show days, AFL Grand Final Friday, regional and part-day holidays) are not included. Check the list against the state government's published calendar every year. Holiday changes recalculate open timesheets.
      </Alert>
      <Card
        title="Public holidays"
        tight
        actions={
          <div className="row">
            <Select className="sm" aria-label="State" value={st} onChange={(e) => setState(e.target.value as AuState)} style={{ width: 90 }}>
              {AU_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
            <Select className="sm" aria-label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 90 }}>
              {[-1, 0, 1, 2].map((d) => {
                const y = new Date().getFullYear() + d;
                return <option key={y}>{y}</option>;
              })}
            </Select>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await api.importPublicHolidays({ state: st, year });
                  reload();
                  toast.success(r.created ? `Imported ${r.created} holiday${r.created === 1 ? '' : 's'}` : 'All usual holidays are already listed');
                } catch (e) {
                  toast.error(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Import usual holidays
            </Button>
          </div>
        }
      >
        <QueryState q={q}>
          {(list) =>
            list.length === 0 ? (
              <EmptyState title={`No holidays for ${st} in ${year}`}>Import the usual holidays or add them one by one.</EmptyState>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Holiday</th>
                    <th>State</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((h) => (
                    <tr key={h.id}>
                      <td className="nowrap">{fmtDate(h.date)}</td>
                      <td className="strong">{h.name}</td>
                      <td>{h.state}</td>
                      <td className="actions">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={async () => {
                            try {
                              await api.deletePublicHoliday(h.id);
                              reload();
                              toast.success('Holiday removed');
                            } catch (e) {
                              toast.error(e);
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </QueryState>
        <form
          className="toolbar"
          style={{ borderTop: '1px solid var(--border)', borderBottom: 0 }}
          onSubmit={async (e) => {
            e.preventDefault();
            setAddError(null);
            try {
              await api.createPublicHoliday({ state: st, date, name: name.trim() });
              setDate('');
              setName('');
              reload();
              toast.success('Holiday added');
            } catch (err) {
              setAddError(err);
            }
          }}
        >
          <Input type="date" className="sm" aria-label="Holiday date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 160 }} required />
          <Input className="sm" aria-label="Holiday name" placeholder="Holiday name" value={name} onChange={(e) => setName(e.target.value)} style={{ width: 260 }} required />
          <Button type="submit" size="sm" disabled={!date || !name.trim()}>
            Add for {st}
          </Button>
          {addError != null && <ErrorAlert error={addError} />}
        </form>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------ privacy */

export function PrivacySettings() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: qk.privacy, queryFn: api.privacyNotices });
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [shown, setShown] = useState<number | null>(null);

  return (
    <div className="stack">
      <QueryState q={q}>
        {(list) => {
          const current = list[0];
          return (
            <>
              <Card title={current ? `Current notice: version ${current.version}` : 'No privacy notice published'}>
                {current ? (
                  <div className="stack">
                    <div className="small muted">Published {fmtDateTime(current.publishedAt, tz)}</div>
                    <h3>{current.title}</h3>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{current.body}</div>
                  </div>
                ) : (
                  <p className="muted">Employees are shown the notice in the app and must acknowledge it before they can punch with location.</p>
                )}
              </Card>
              {list.length > 1 && (
                <Card title="Earlier versions" tight>
                  <table className="table">
                    <tbody>
                      {list.slice(1).map((n) => (
                        <tr key={n.version}>
                          <td>
                            <Badge>v{n.version}</Badge>
                          </td>
                          <td className="strong">{n.title}</td>
                          <td className="nowrap small">{fmtDateTime(n.publishedAt, tz)}</td>
                          <td className="actions">
                            <Button size="sm" variant="ghost" onClick={() => setShown(shown === n.version ? null : n.version)}>
                              {shown === n.version ? 'Hide' : 'Read'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {shown != null && <div className="card-body" style={{ whiteSpace: 'pre-wrap' }}>{list.find((n) => n.version === shown)?.body}</div>}
                </Card>
              )}
            </>
          );
        }}
      </QueryState>
      {can('owner') ? (
        <Card title="Publish a new version">
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              setConfirming(true);
            }}
          >
            <ErrorAlert error={error} />
            <Field label="Title" required>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
            </Field>
            <Field label="Notice text" required hint="Plain text. Explain what is collected (punch times, location at punch, kiosk photos), why, who sees it and how long it is kept.">
              <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} required minLength={20} />
            </Field>
            <div className="row end">
              <Button
                onClick={() => {
                  const c = q.data?.[0];
                  if (c) {
                    setTitle(c.title);
                    setBody(c.body);
                  }
                }}
                disabled={!q.data?.length}
              >
                Start from current
              </Button>
              <Button type="submit" variant="primary" disabled={!title.trim() || body.trim().length < 20}>
                Publish
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Alert tone="accent">Only the owner can publish a privacy notice.</Alert>
      )}
      {confirming && (
        <PromptDialog
          hideNote
          title="Publish privacy notice"
          description="Every employee is asked to acknowledge the new version the next time they open the app."
          confirmLabel="Publish"
          onClose={() => setConfirming(false)}
          onConfirm={async () => {
            setError(null);
            try {
              await api.publishPrivacyNotice({ title: title.trim(), body: body.trim() });
              await queryClient.invalidateQueries({ queryKey: qk.privacy });
              setTitle('');
              setBody('');
              toast.success('Privacy notice published');
            } catch (e) {
              setError(e);
            }
          }}
        />
      )}
    </div>
  );
}
